/**
 * Per-SITE catalog behind the patient "Send a form / Send a text / Send an email" popup
 * (PatientSidebar.vue and the three panels that extend it).
 *
 * WHY THIS EXISTS (2026-10-09)
 * ----------------------------
 * The popup used to make four SERIAL round trips before its skeleton went away (forms with every
 * form's ConfigData — 3.1 MB on a 224-form site —, then packets, then the wiki templates, then a
 * site-tag list nobody read), and again on EVERY open. The lists are per SITE, not per patient, so
 * this module:
 *   - keeps the last good copy of each list for MAX_AGE_MS, keyed by site + what (forms, packets,
 *     wiki:<tag>), and shares ONE in-flight request between concurrent callers;
 *   - lets the popup show a fresh-enough copy at once and refresh it in the background
 *     (stale-while-revalidate) — the component decides what to do when the refresh lands;
 *   - prefetches the lists, silently, when a patient profile opens, so the FIRST click is instant.
 *
 * RULES
 *   - A failed load is NEVER cached: a loader that throws, or resolves undefined/null (the store
 *     actions' way of saying "failed"), leaves the cache exactly as it was, so the next open retries.
 *   - invalidateSendCatalog() is called by the store actions that change these lists (save/delete
 *     form, packet or wiki page). A load that STARTED before an invalidation is not cached when it
 *     lands, so a pre-save response can never outlive the save.
 *   - Every read hands out a COPY. The component makes its data reactive and may mutate it; the
 *     cached original must stay pristine.
 *   - The prefetch is side-effect free: it dispatches the store actions with `silent: true`
 *     (no alert/* dispatch, no wiki commit) and swallows every failure.
 *
 * Intentionally pure and IMPORT-FREE — the node-tests execute this exact file. The request shapes
 * below must equal the ones PatientSidebar.loadLists() builds (apart from `silent`) or the prefetch
 * fills keys the popup never reads; tests/component/sendPanelCatalog.spec.js pins that agreement.
 */

export const MAX_AGE_MS = 10 * 60 * 1000;

/** Delay before the post-mount prefetch, so it never competes with the profile's own first requests. */
export const PREFETCH_DELAY_MS = 2000;

const entries = new Map(); // key -> { data, at }   settled successes only
const pending = new Map(); // key -> { promise }     the one in-flight load per key
let epoch = 0;             // bumped by every invalidation; a load only caches if it did not change

/** Cache key: "<siteID>|forms", "<siteID>|packets" or "<siteID>|wiki:<tag>". */
export function catalogKey(kind, payload) {
  const p = payload || {};
  const what = kind === 'wiki' ? `wiki:${p.tags || ''}` : kind;
  return `${p.siteID || ''}|${what}`;
}

const groupOf = (key) => String(key.split('|')[1] || '').split(':')[0];

const copy = (data) => (data === undefined || data === null ? data : JSON.parse(JSON.stringify(data)));

function freshEntry(key, opts) {
  const e = entries.get(key);
  if (!e) return null;
  const now = opts && typeof opts.now === 'number' ? opts.now : Date.now();
  const max = (opts && opts.maxAgeMs) || MAX_AGE_MS;
  const age = now - e.at;
  return age < 0 || age >= max ? null : e;
}

/** A COPY of the cached data when it is younger than MAX_AGE_MS, else null. */
export function peekCatalog(key, opts) {
  const e = freshEntry(key, opts);
  return e ? { data: copy(e.data), at: e.at } : null;
}

export function isCatalogPending(key) {
  return pending.has(key);
}

/**
 * Run `loader` once per key at a time: a second caller while one is in flight gets the same request.
 * Resolves a copy of the loader's result (undefined/null when it "failed" — never cached); rejects if
 * the loader throws (also never cached). Concurrent callers each get their own copy.
 */
export function loadCatalog(key, loader) {
  let rec = pending.get(key);
  if (!rec) {
    const started = epoch;
    const promise = (async () => loader())();
    rec = { promise };
    pending.set(key, rec);
    const mine = rec;
    // Registered BEFORE any caller's continuation, so the cache is already filled when they resume.
    promise.then(
      (data) => {
        if (pending.get(key) === mine) pending.delete(key);
        if (data !== undefined && data !== null && started === epoch) {
          entries.set(key, { data: copy(data), at: Date.now() });
        }
      },
      () => { if (pending.get(key) === mine) pending.delete(key); },
    );
  }
  return rec.promise.then(copy);
}

/**
 * Forget what the app itself just changed. `group` is 'forms', 'packets' or 'wiki'; omitted = all.
 * In-flight loads of that group are detached (the next caller starts a fresh request) and will not
 * be cached when they land.
 */
export function invalidateSendCatalog(group) {
  epoch += 1;
  Array.from(entries.keys()).forEach((k) => { if (!group || groupOf(k) === group) entries.delete(k); });
  Array.from(pending.keys()).forEach((k) => { if (!group || groupOf(k) === group) pending.delete(k); });
}

/** Everything, for tests. */
export function resetSendCatalog() {
  invalidateSendCatalog();
}

/**
 * The lists a patient profile can open a send popup on: forms (no ConfigData — nothing in the
 * popup reads it), packets, and the three wiki template tags. `silent` is added at call time.
 */
function prefetchPlan(siteID) {
  return [
    { kind: 'forms', action: 'forms/getForms', payload: { siteID, includeConfigData: false } },
    { kind: 'packets', action: 'forms/getPackets', payload: { siteID } },
    { kind: 'wiki', action: 'wiki/getWikiPages', payload: { siteID, tags: 'form' } },
    { kind: 'wiki', action: 'wiki/getWikiPages', payload: { siteID, tags: 'sms-template' } },
    { kind: 'wiki', action: 'wiki/getWikiPages', payload: { siteID, tags: 'email-template' } },
  ];
}

/** The requests the prefetch makes — exported so a test can compare them with the popup's. */
export function sendCatalogPrefetchRequests(siteID) {
  return prefetchPlan(siteID).map((r) => ({ ...r, key: catalogKey(r.kind, r.payload) }));
}

/**
 * Warm the catalog for `siteID`. Skips anything already cached (fresh) or already loading.
 * `dispatch` is the Vuex store's dispatch. Never throws, never rejects, never touches the UI.
 */
export function prefetchSendCatalog(dispatch, siteID) {
  if (!siteID || typeof dispatch !== 'function') return;
  prefetchPlan(siteID).forEach(({ kind, action, payload }) => {
    const key = catalogKey(kind, payload);
    if (freshEntry(key) || pending.has(key)) return;
    try {
      loadCatalog(key, () => dispatch(action, { ...payload, silent: true })).catch(() => {});
    } catch (e) { /* a prefetch is best-effort */ }
  });
}

/**
 * prefetchSendCatalog() after a short delay. Fire-and-forget: if the profile closes in the
 * meantime the prefetch is still harmless (silent, idempotent, deduped). Returns the timer id.
 * @param {{dispatch: Function}} store  the Vuex store
 */
export function scheduleSendCatalogPrefetch(store, siteID, delayMs) {
  if (!store || typeof store.dispatch !== 'function' || !siteID) return null;
  return setTimeout(
    () => prefetchSendCatalog((action, payload) => store.dispatch(action, payload), siteID),
    typeof delayMs === 'number' ? delayMs : PREFETCH_DELAY_MS,
  );
}
